import { createContext, useEffect, useState, useRef, useCallback } from "react";
import io from "socket.io-client";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { SetChatHistory, SetPagerModalOpen, SetCurrentPagerData, SetChatListTrigger, SetConversationUser, SetChatParams, SetSelectedChatId, SetCurrentRawChatData, SetCurrentPatientDataForChat, ClearMessageToEdit, SetNonAppChatEnded, SetIsFirstMessageSentToNonAppUser, SetIsChatStarting, IncrementMessageBadge, IncrementPagerBadge, SetPagerListTrigger } from "../redux/slices/app";
import { formatChatTime } from "../utils/formatTime";
import {
  startOutgoingCall,
  receiveIncomingCall,
  acceptCall,
  endCall,
  denyCall,
  resetCallState,
} from "../redux/slices/call";
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import { PATH_DASHBOARD } from "../routes/paths";
import { PROFILE_IMAGE_URL } from "../config";

const BASE_ATTACHMENT_URL = 'https://meftiicomm.com:3000/media/chats/';

const getAbsoluteUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BASE_ATTACHMENT_URL}${url}`;
};

const initialState = {
  socket: null,
  isConnected: false,
  connect: () => { },
  disconnect: () => { },
  sendChatMessage: () => { },
  sendGroupChatMessage: () => { },
  makeCall: () => { },
  denyCall: () => { },
  acceptCall: () => { },
  endCurrentCall: () => { },
  sendTextCallRequest: () => { },
  playBeep: () => { },
  stopBeep: () => { },
  endConversation: () => { },
  sendPager: () => { },
  BASE_URL: process.env.REACT_APP_BASE_URL,
  CALLING_URL: process.env.REACT_APP_CALLING_URL,
};

const SocketContext = createContext(initialState);

const generateUUID = () => {
  let cryptoObj = window.crypto || window.msCrypto;
  let uuid = cryptoObj.getRandomValues(new Uint32Array(4));
  return (
    uuid[0].toString(16).padStart(8, "0") +
    "-" +
    uuid[1].toString(16).padStart(4, "0") +
    "-" +
    uuid[2].toString(16).padStart(4, "0") +
    "-" +
    uuid[3].toString(16).padStart(4, "0") +
    "-" +
    cryptoObj
      .getRandomValues(new Uint8Array(6))
      .reduce((acc, val) => acc + ("0" + val.toString(16)).slice(-2), "")
  );
};

const getCurrentTime = () => {
  return Math.round(Date.now() / 1000).toString();
};

const generateTimeStamp = () => {
  return Math.floor(new Date().getTime() / 1000);
};

const generateRoomId = (callerId) => {
  const letters = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ01234456789";
  let randomString = "";
  for (let i = 0; i < 10; i++) {
    const rand = Math.floor(Math.random() * letters.length);
    randomString += letters.charAt(rand);
  }
  return callerId + randomString;
};

const getDisabledStatus = async (receiverId) => {
  try {
    const response = await axios.post('https://backend-stgweb.meftii.com/getdisabledstatus', { callingId: receiverId });
    return response.data;
  } catch (error) {
    console.error("Error fetching disabled status:", error);
    return { callStatus: "false", data: { doNotDisturbEnable: false } };
  }
};

const SocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);
  const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || "https://meftiicomm.com:8081";
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const CALLING_URL = process.env.REACT_APP_CALLING_URL;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { chatParams, chatHistory, currentPatientDataForChat, isNonAppChatEnded, isFirstMessageSentToNonAppUser } = useSelector((store) => store.app);
  const { activeCallDetails, ringingModalOpen } = useSelector((store) => store.call);

  const chatHistoryRef = useRef(chatHistory);
  useEffect(() => {
    chatHistoryRef.current = chatHistory;
  }, [chatHistory]);

  const [currentUser, setCurrentUser] = useState(() => {
    const storedUser = sessionStorage.getItem('currentUser');
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const activeCallDetailsRef = useRef(activeCallDetails);
  const chatParamsRef = useRef(chatParams);
  const currentUserRef = useRef(currentUser);
  const currentPatientDataForChatRef = useRef(currentPatientDataForChat);
  const isNonAppChatEndedRef = useRef(isNonAppChatEnded);
  const isFirstMessageSentToNonAppUserRef = useRef(isFirstMessageSentToNonAppUser);

  useEffect(() => {
    activeCallDetailsRef.current = activeCallDetails;
  }, [activeCallDetails]);

  useEffect(() => {
    chatParamsRef.current = chatParams;
  }, [chatParams]);

  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  useEffect(() => {
    currentPatientDataForChatRef.current = currentPatientDataForChat;
  }, [currentPatientDataForChat]);

  useEffect(() => {
    isNonAppChatEndedRef.current = isNonAppChatEnded;
  }, [isNonAppChatEnded]);

  useEffect(() => {
    isFirstMessageSentToNonAppUserRef.current = isFirstMessageSentToNonAppUser;
  }, [isFirstMessageSentToNonAppUser]);

  const lastCallEndRef = useRef(0);

  useEffect(() => {
    const handleStorageChange = () => {
      const storedUser = sessionStorage.getItem('currentUser');
      setCurrentUser(storedUser ? JSON.parse(storedUser) : null);
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const audioBeepRef = useRef(null);

  const stopBeep = useCallback(() => {
    if (audioBeepRef.current) {
      audioBeepRef.current.pause();
      audioBeepRef.current.src = '';
      audioBeepRef.current = null;
    }
  }, []);

  const playBeep = useCallback((param) => {
    if (audioBeepRef.current) {
      audioBeepRef.current.pause();
      audioBeepRef.current.src = '';
      audioBeepRef.current = null;
    }

    if (param === "incoming") {
      audioBeepRef.current = new Audio('/soft.mp3');
    } else if (param === "outgoing") {
      audioBeepRef.current = new Audio('/vip.mp3');
    }

    if (audioBeepRef.current) {
      audioBeepRef.current.loop = true;
      audioBeepRef.current.play().catch(e => console.error("Error playing sound:", e));
    }
  }, []);

  const playMessageSound = useCallback(() => {
    const audio = new Audio('/message.mp3');
    audio.play()
      .then(() => {
        setTimeout(() => {
          audio.pause();
          audio.currentTime = 0;
        }, 1500);
      })
      .catch(e => console.error("Error playing notification sound:", e));
  }, []);

  const callAddPatientRecordApi = useCallback(async (patientId, chatId, chatType, orgId, addedBy, token) => {
    try {
      const payload = {
        patientId: patientId,
        chatId: chatId,
        chat_type: chatType,
        orgId: orgId,
        addedBy: addedBy,
      };
      const response = await axios.post('https://backend-stgweb.meftii.com/addPatientRecord', payload, {
        headers: { Authorization: token },
      });
      if (response.data?.status === 'true') {
        toast.success("Patient record added to chat successfully!");
      } else {
        toast.error(response.data?.message || "Failed to add patient record to chat.");
      }
    } catch (error) {
      console.error('Error calling addPatientRecord API from SocketContext:', error);
      toast.error("An error occurred while adding patient record to chat.");
    } finally {
      dispatch(SetCurrentPatientDataForChat(null));
    }
  }, [dispatch]);

  const emitChatbox = useCallback(() => {
    currentUserRef.current = sessionStorage.getItem('currentUser') ? JSON.parse(sessionStorage.getItem('currentUser')) : null;

    if (socketRef.current && currentUserRef.current && currentUserRef.current.callingId) {
      console.log("[SocketContext] Emitting chatbox event for user:", currentUserRef.current.callingId);
      console.log(`[SocketContext] Emitting 'chatbox'. Socket connected: ${socketRef.current.connected}, ID: ${socketRef.current.id}`);
      socketRef.current.emit("chatbox", {
        user_id: currentUserRef.current.callingId,
        timestamp: getCurrentTime(),
      });
    } else {
      console.warn("[SocketContext] Cannot emit chatbox: Socket or user callingId missing.");
    }
  }, []);

  // Helper function to verify connection and emit chatbox if needed
  const ensureSocketConnection = useCallback(async () => {
    // 1. If already connected, return true immediately.
    if (socketRef.current?.connected) {
      return true;
    }

    // 2. If socket instance exists but disconnected, connect it.
    if (socketRef.current) {
      console.log("[SocketContext] Socket disconnected. Connecting...");
      socketRef.current.connect();
    } else {
      console.error("[SocketContext] Socket instance not found.");
      return false;
    }

    // 3. Wait for connection
    for (let i = 0; i < 25; i++) { // Wait up to 5 seconds (25 * 200ms)
      if (socketRef.current?.connected) {
        console.log("[SocketContext] Socket reconnected. Emitting chatbox...");
        // 4. Emit chatbox once connected
        emitChatbox();
        // 5. Small buffer to ensure server processes the event
        await new Promise(resolve => setTimeout(resolve, 500));
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    console.error("[SocketContext] Failed to reconnect socket.");
    return false;
  }, [emitChatbox]);

  const safeEmit = useCallback(async (event, data) => {
    const connected = await ensureSocketConnection();
    if (connected && socketRef.current) {
      // Always emit chatbox before the actual event
      emitChatbox();
      console.log(`[SocketContext] Emitting '${event}'. Socket connected: ${socketRef.current.connected}, ID: ${socketRef.current.id}`);
      socketRef.current.emit(event, data);
      return true;
    } else {
      console.error(`[SocketContext] Failed to emit ${event}: Socket not connected.`);
      // toast.error("Connection lost. Retrying...");
      return false;
    }
  }, [ensureSocketConnection]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log("[SocketContext] Tab visible. Checking connection...");
        ensureSocketConnection();
      }
    };

    const handleOnline = () => {
      console.log("[SocketContext] Browser is back online. Re-syncing socket...");
      ensureSocketConnection();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [ensureSocketConnection]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
          const timeSinceCallEnd = Date.now() - lastCallEndRef.current;
          if (timeSinceCallEnd < 8000) {
            console.warn("[Network Guard] Ignoring network error during call teardown window (QUIC suppression).");
          } else {
            console.warn("[Network Guard] Axios network error detected (potentially QUIC/HTTP3 issue). Checking socket health...");
            ensureSocketConnection();
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [ensureSocketConnection]);

  const currentUserId = currentUser?.userId || currentUser?.id;
  const currentUserEmail = currentUser?.email;
  const currentUserCallingId = currentUser?.callingId;

  const connect = useCallback(() => {
    if (socketRef.current) {
      if (!socketRef.current.connected) {
        socketRef.current.connect();
      }
    }
  }, []);

  useEffect(() => {
    const socketOptions = {
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
      reconnectionDelayMax: 10000,
    };

    if (currentUser) {
      console.log("[SocketContext] currentUser details found:", { id: currentUserId, callingId: currentUserCallingId });
      socketOptions.query = {
        userId: currentUserId,
        email: currentUserEmail,
        callingId: currentUserCallingId,
      };
    } else {
      console.warn("[SocketContext] currentUser is NULL. Socket will connect without credentials.");
    }

    console.log("[SocketContext] Initializing socket with user:", currentUserId);
    const socket = io(SOCKET_URL, socketOptions);
    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("Socket connected:", socket.id);
      setIsConnected(true);
      emitChatbox();
    });

    socket.on("disconnect", (reason) => {
      console.log(`[SocketContext] Socket disconnected. ID: ${socket.id}, Reason: ${reason}`);
      setIsConnected(false);

      if (reason === "io server disconnect" || reason === "io client disconnect") {
        // If it's a client disconnect but we didn't mean to (e.g. socketRef still exists)
        // we attempt to reconnect.
        if (socketRef.current && !socketRef.current.connected) {
          console.log("[SocketContext] Attempting to reconnect after disconnect...");
          socket.connect();
        }
      }
    });

    socket.on("connect_error", (error) => {
      console.error("[SocketContext] Socket connection error:", error);
      setIsConnected(false);

      // If we see a persistent error, force a disconnect and restart
      setTimeout(() => {
        if (socketRef.current && !socketRef.current.connected) {
          console.log("[SocketContext] Persistent error state. Force-restarting socket...");
          socketRef.current.disconnect().connect();
        }
      }, 5000);
    });

    socket.on("reconnect", (attemptNumber) => {
      console.log("[SocketContext] Socket reconnected after", attemptNumber, "attempts");
      setIsConnected(true);
      emitChatbox();
    });

    socket.on("groupInvitation", (data) => {
      console.log("--- SOCKET EVENT: groupInvitation received ---", data);
      dispatch(SetChatListTrigger());

      const currentUserVal = currentUserRef.current;
      const patientDataForChat = currentPatientDataForChatRef.current;

      if (!currentUserVal) {
        dispatch(SetIsChatStarting(false));
        return;
      }

      const groupId = data._id;
      const groupName = data.group_name;
      const groupImage = data.group_image;

      if (groupId && groupName) {
        const myId = currentUserVal.callingId || currentUserVal.userId || currentUserVal.id;
        const myName = `${currentUserVal.firstName || ''} ${currentUserVal.lastName || ''}`.trim();

        dispatch(SetSelectedChatId(`${groupId}`));
        dispatch(SetChatParams({
          receiverId: groupId,
          contact_name: groupName,
          senderId: myId,
          sender_name: myName,
          chat_type: 'group',
        }));
        dispatch(SetConversationUser({
          name: groupName,
          senderId: groupId,
          profilePicture: groupImage,
        }));
        dispatch(SetCurrentRawChatData(data));

        if (patientDataForChat && patientDataForChat.patientId) {
          callAddPatientRecordApi(
            patientDataForChat.patientId,
            `${groupId}${myId}`,
            patientDataForChat.chatType,
            patientDataForChat.orgId,
            patientDataForChat.addedBy,
            patientDataForChat.chatToken
          );
        }

        navigate(PATH_DASHBOARD.general.app);
        dispatch(SetIsChatStarting(false));
      } else {
        dispatch(SetIsChatStarting(false));
      }
    });

    socket.on("decryptedGroupMsg", (data) => {
      const message_data = data.message_data || data.data?.message_data || data.data?.data?.message_data;
      const decryptedText = data.decryptedtext || data.data?.decryptedtext || message_data?.message;

      const currentChatParams = chatParamsRef.current;
      const currentUserVal = currentUserRef.current;

      if (!message_data || !currentChatParams || !currentUserVal) return;

      const currentChatHistory = chatHistoryRef.current;
      const isOutgoing = String(message_data.member_id) === String(currentChatParams?.senderId);
      const isIncoming = !isOutgoing;

      if (isOutgoing) return;

      const rawChatTime = message_data.chat_time;
      const formattedTime = formatChatTime(rawChatTime);
      const messageType = (message_data.message_type || 'text').toLowerCase().trim();
      const senderName = message_data.member_name || 'Unknown';

      let transformedMsg = {
        type: 'msg',
        incoming: true,
        outgoing: false,
        time: formattedTime,
        senderName: senderName,
        chatType: 'group',
        message_id: message_data.message_id,
        message_data: {
          ...message_data,
          message_text: decryptedText,
          chat_time: parseInt(rawChatTime),
        }
      };

      switch (messageType) {
        case 'text':
        case 'isedited':
        case 'isdelete':
          transformedMsg.subtype = 'text';
          transformedMsg.message = decryptedText;
          break;
        case 'document':
        case 'file':
          transformedMsg.subtype = 'doc';
          transformedMsg.message = message_data.message || 'Document';
          transformedMsg.fileName = message_data.file_name || decryptedText.split('/').pop() || 'File';
          transformedMsg.fileUrl = getAbsoluteUrl(decryptedText);
          break;
        case 'image':
        case 'sticker':
        case 'gif':
          transformedMsg.subtype = 'image';
          transformedMsg.message = message_data.message || '';
          transformedMsg.img = getAbsoluteUrl(decryptedText);
          break;
        case 'video':
          transformedMsg.subtype = 'video';
          transformedMsg.message = message_data.message || '';
          transformedMsg.videoUrl = getAbsoluteUrl(decryptedText);
          break;
        case 'audio':
          transformedMsg.subtype = 'audio';
          transformedMsg.message = 'Voice message';
          transformedMsg.audioUrl = getAbsoluteUrl(decryptedText);
          break;
        case 'location':
          transformedMsg.subtype = 'location';
          transformedMsg.message = message_data.message || 'Location Shared';
          const LocationPlaceholderImage2 = 'https://miro.medium.com/v2/resize:fit:1200/1*8ZhgsyCNQRDaKEB7Rk_Udw.jpeg';
          transformedMsg.preview = getAbsoluteUrl(message_data.location_preview || decryptedText) || LocationPlaceholderImage2;
          transformedMsg.locationName = message_data.locationName || 'Shared Location';
          transformedMsg.locationUrl = (message_data.lat && message_data.lon)
            ? `https://www.google.com/maps/search/?api=1&query=${message_data.lat},${message_data.lon}`
            : '';
          transformedMsg.senderName = senderName;
          break;
        case 'contact':
          transformedMsg.subtype = 'contact';
          transformedMsg.message = message_data.message || 'Contact shared';
          transformedMsg.contactName = message_data.contact_name || decryptedText || 'Unknown Contact';
          transformedMsg.contactPhone = message_data.contact_phone_no || '';
          transformedMsg.contactEmail = message_data.contact_email || '';
          break;
        case 'link':
          transformedMsg.subtype = 'link';
          transformedMsg.message = message_data.message || '';
          transformedMsg.preview = getAbsoluteUrl(message_data.preview_url || decryptedText);
          transformedMsg.linkTitle = message_data.link_title || 'Shared Link';
          transformedMsg.linkUrl = message_data.link_url || decryptedText;
          break;
        case 'reply':
          transformedMsg.subtype = 'reply';
          transformedMsg.message = decryptedText;
          transformedMsg.reply = message_data.reply_text || message_data.reply || '';
          break;
        default:
          transformedMsg.subtype = 'text';
          transformedMsg.message = decryptedText;
          break;
      }

      const isForActiveChat = (currentChatParams?.chat_type === "group" && message_data.group_id === currentChatParams?.receiverId);

      if (isForActiveChat && isIncoming) {
        if (messageType === 'isedited' || messageType === 'isdelete') {
          const updatedHistory = currentChatHistory.map(msg =>
            (msg.message_data?.message_id || msg.message_id) === (message_data.message_id || message_data.message_data?.message_id)
              ? { ...msg, message: transformedMsg.message, time: transformedMsg.time, message_data: transformedMsg.message_data }
              : msg
          );
          dispatch(SetChatHistory(updatedHistory));
        } else {
          dispatch(SetChatHistory([...currentChatHistory, transformedMsg]));
        }
        playMessageSound();
      } else if (isIncoming) {
        dispatch(IncrementMessageBadge());
        playMessageSound();
        const senderProfileImageUrl = message_data.member_id
          ? `${PROFILE_IMAGE_URL}${message_data.member_id}?t=${new Date().getTime()}`
          : null;

        toast.custom((t) => (
          <Box
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: 1,
              boxShadow: 3,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              pointerEvents: 'auto',
              minWidth: 300,
              maxWidth: 400,
              borderLeft: (theme) => `6px solid ${theme.palette.primary.main}`,
            }}
            onClick={() => {
              toast.dismiss(t.id);
              const targetId = message_data.group_id;
              const targetName = message_data.group_name || "Group Chat";
              const myId = currentUserVal?.callingId || currentUserVal?.userId;
              const myName = `${currentUserVal?.firstName} ${currentUserVal?.lastName}`;

              dispatch(SetConversationUser({
                name: targetName,
                senderId: targetId,
                profilePicture: null
              }));
              dispatch(SetChatParams({
                receiverId: targetId,
                contact_name: targetName,
                senderId: myId,
                sender_name: myName,
                chat_type: 'group',
              }));
              navigate(PATH_DASHBOARD.general.app);
            }}
          >
            <Avatar alt={transformedMsg.senderName} src={senderProfileImageUrl} />
            <Stack sx={{ ml: 2, overflow: 'hidden' }}>
              <Typography variant="subtitle2" noWrap>{transformedMsg.senderName} (Group)</Typography>
              <Typography variant="body2" color="text.secondary" noWrap>{transformedMsg.message}</Typography>
            </Stack>
          </Box>
        ), { position: 'top-right', duration: 5000 });
      }
      dispatch(SetChatListTrigger());
    });

    socket.on("msgFromGroup", (data) => {
      // Handle isDelete messages directly without decryption
      if (data.message_type === "isDelete") {
        const message_data = data;
        const currentChatParams = chatParamsRef.current;
        const currentUserVal = currentUserRef.current;

        if (!message_data || !currentChatParams || !currentUserVal) return;

        const currentChatHistory = chatHistoryRef.current;
        const isOutgoing = String(message_data.member_id) === String(currentChatParams?.senderId);
        const isIncoming = !isOutgoing;

        if (isOutgoing) return;

        const rawChatTime = message_data.chat_time;
        const formattedTime = formatChatTime(rawChatTime);
        const senderName = message_data.member_name || 'Unknown';

        const transformedMsg = {
          type: 'msg',
          incoming: true,
          outgoing: false,
          time: formattedTime,
          senderName: senderName,
          chatType: 'group',
          message_id: message_data.message_id,
          subtype: 'text',
          message: "⊘ This message was deleted",
          message_data: {
            ...message_data,
            message_text: "⊘ This message was deleted",
            chat_time: parseInt(rawChatTime),
          }
        };

        const isForActiveChat = (currentChatParams?.chat_type === "group" && message_data.group_id === currentChatParams?.receiverId);

        if (isForActiveChat && isIncoming) {
          const updatedHistory = currentChatHistory.map(msg =>
            (msg.message_data?.message_id || msg.message_id) === (message_data.message_id || message_data.message_data?.message_id)
              ? { ...msg, message: transformedMsg.message, time: transformedMsg.time, message_data: transformedMsg.message_data }
              : msg
          );
          dispatch(SetChatHistory(updatedHistory));
          playMessageSound();
        } else if (isIncoming) {
          dispatch(IncrementMessageBadge());
          playMessageSound();
        }
        dispatch(SetChatListTrigger());
        return;
      }

      console.log(`[SocketContext] Emitting 'decryptGroupMsg'. Socket connected: ${socket.connected}, ID: ${socket.id}`);
      // Emit chatbox before decryptGroupMsg
      if (currentUserRef.current?.callingId) {
        socket.emit("chatbox", { user_id: currentUserRef.current.callingId, timestamp: getCurrentTime() });
      }
      socket.emit("decryptGroupMsg", {
        data: {
          message_data: data,
          receiver_id: chatParamsRef.current?.senderId,
        }
      });
    });

    socket.on("viewchat", (data) => {
      dispatch(SetChatListTrigger());
    });

    socket.on("receivechat", (data) => {
      // Handle isDelete messages directly without decryption
      const message_data = data.message_data || data;
      if (message_data.message_type === "isDelete") {
        const currentChatParams = chatParamsRef.current;
        const currentUserVal = currentUserRef.current;

        if (!message_data || !currentChatParams || !currentUserVal) return;

        const currentChatHistory = chatHistoryRef.current;
        const isOutgoing = String(message_data.sender_id) === String(currentChatParams?.senderId);
        const isIncoming = !isOutgoing;

        if (isOutgoing) return;

        const rawChatTime = message_data.chat_time;
        const formattedTime = formatChatTime(rawChatTime);

        const transformedMsg = {
          type: 'msg',
          incoming: true,
          outgoing: false,
          time: formattedTime,
          senderName: message_data.sender_name || message_data.member_name || 'Unknown',
          chatType: currentChatParams?.chat_type,
          message_id: message_data.message_id,
          subtype: 'text',
          message: "⊘ This message was deleted",
          message_data: {
            ...message_data,
            message_text: "⊘ This message was deleted",
            chat_time: parseInt(rawChatTime),
          }
        };

        const isForActiveChat = (currentChatParams?.chat_type === "group" && message_data.group_id === currentChatParams?.receiverId) ||
          (currentChatParams?.chat_type === "single" && message_data.sender_id === currentChatParams?.receiverId);

        if (isForActiveChat && isIncoming) {
          const updatedHistory = currentChatHistory.map(msg =>
            (msg.message_data?.message_id || msg.message_id) === (message_data.message_id || message_data.message_data?.message_id)
              ? { ...msg, message: transformedMsg.message, time: transformedMsg.time, message_data: transformedMsg.message_data }
              : msg
          );
          dispatch(SetChatHistory(updatedHistory));
          playMessageSound();
        } else if (isIncoming) {
          dispatch(IncrementMessageBadge());
          playMessageSound();
        }
        dispatch(SetChatListTrigger());
        return;
      }

      console.log(`[SocketContext] Emitting 'decryptMsg'. Socket connected: ${socket.connected}, ID: ${socket.id}`);
      // Emit chatbox before decryptMsg
      if (currentUserRef.current?.callingId) {
        socket.emit("chatbox", { user_id: currentUserRef.current.callingId, timestamp: getCurrentTime() });
      }
      socket.emit("decryptMsg", data);
      dispatch(SetChatListTrigger());
    });

    socket.on("encryptedMsg", async (data) => {
      const currentChatParams = chatParamsRef.current;
      const currentUserVal = currentUserRef.current;

      if (!currentChatParams || !currentUserVal) return;

      const { encryptedtext, message_type, receiver_id } = data;
      const original_message_data = data.message_data;

      let payload_for_next_emit = { ...original_message_data };
      const isTextType = ['text', 'isEdited', 'isDelete'].includes(message_type);
      if (isTextType) {
        payload_for_next_emit.message = encryptedtext;
        delete payload_for_next_emit.attachment;
      } else {
        payload_for_next_emit.attachment = encryptedtext;
        payload_for_next_emit.message = original_message_data.message || "";
      }

      const sender_id = currentChatParams.senderId;
      const actual_receiver_id_from_event = receiver_id;

      let chat_id_for_payload;
      if (payload_for_next_emit.chat_type === 'group') {
        chat_id_for_payload = `${actual_receiver_id_from_event}`;
      } else {
        const sortedIds = [String(sender_id), String(actual_receiver_id_from_event)].sort();
        chat_id_for_payload = `${sortedIds[0]}${sortedIds[1]}`;
      }
      payload_for_next_emit.chat_id = chat_id_for_payload;
      payload_for_next_emit.receiver_id = actual_receiver_id_from_event;
      payload_for_next_emit.sender_id = sender_id;
      payload_for_next_emit.user_id = sender_id;
      payload_for_next_emit.user_name = currentChatParams.contact_name;
      payload_for_next_emit.sender_name = original_message_data.sender_name || currentChatParams.sender_name;

      if (payload_for_next_emit.chat_type === 'group') {
        payload_for_next_emit.group_name = currentChatParams.contact_name;
        payload_for_next_emit.member_id = sender_id;
        payload_for_next_emit.member_name = payload_for_next_emit.sender_name;
        payload_for_next_emit.chat_id = payload_for_next_emit.group_id;
        payload_for_next_emit.receiver_id = payload_for_next_emit.group_id;
      }

      if (payload_for_next_emit.chat_type === "group") {
        await safeEmit("msgToGroup", payload_for_next_emit);
      } else {
        await safeEmit("startchat", {
          message_data: payload_for_next_emit,
          receiver_id: payload_for_next_emit.receiver_id,
          sender_id: payload_for_next_emit.sender_id,
          sender_name: payload_for_next_emit.sender_name,
          receiver_name: payload_for_next_emit.receiver_name,
        });

        await safeEmit("receivechat", {
          message_data: payload_for_next_emit,
          receiver_id: payload_for_next_emit.receiver_id,
          sender_id: payload_for_next_emit.sender_id,
        });
      }
      dispatch(SetChatListTrigger());
    });

    socket.on("decryptedMsg", (data) => {
      const currentChatParams = chatParamsRef.current;
      const currentUserVal = currentUserRef.current;

      data.message_data.attachment = data.decryptedtext;

      const message_data = data.message_data || {};
      const decryptedText = data.decryptedtext || message_data.message;

      if (!message_data || !decryptedText) return;

      const currentChatHistory = chatHistoryRef.current;
      const isOutgoing = String(message_data.sender_id) === String(currentChatParams?.senderId);
      const isIncoming = !isOutgoing;

      if (isOutgoing) return;

      const rawChatTime = message_data.chat_time;
      const formattedTime = formatChatTime(rawChatTime);
      const messageType = (message_data.message_type || 'text').toLowerCase().trim();

      let transformedMsg = {
        type: 'msg',
        incoming: true,
        outgoing: false,
        time: formattedTime,
        senderName: message_data.sender_name || message_data.member_name || 'Unknown',
        chatType: currentChatParams?.chat_type,
        message_id: message_data.message_id,
        message_data: {
          ...message_data,
          message_text: decryptedText,
          chat_time: parseInt(rawChatTime),
        }
      };

      switch (messageType) {
        case 'text':
        case 'isedited':
        case 'isdelete':
          transformedMsg.subtype = 'text';
          transformedMsg.message = decryptedText;
          break;
        case 'document':
        case 'file':
          transformedMsg.subtype = 'doc';
          transformedMsg.message = message_data.message || 'Document';
          transformedMsg.fileName = message_data.file_name || decryptedText.split('/').pop() || 'File';
          transformedMsg.fileUrl = getAbsoluteUrl(decryptedText);
          break;
        case 'image':
        case 'sticker':
        case 'gif':
          transformedMsg.subtype = 'image';
          transformedMsg.message = message_data.message || '';
          transformedMsg.img = getAbsoluteUrl(decryptedText);
          break;
        case 'video':
          transformedMsg.subtype = 'video';
          transformedMsg.message = message_data.message || '';
          transformedMsg.videoUrl = getAbsoluteUrl(decryptedText);
          break;
        case 'audio':
          transformedMsg.subtype = 'audio';
          transformedMsg.message = 'Voice message';
          transformedMsg.audioUrl = getAbsoluteUrl(decryptedText);
          break;
        case 'location':
          transformedMsg.subtype = 'location';
          transformedMsg.message = message_data.message || 'Location Shared';
          const LocationPlaceholderImage2 = 'https://miro.medium.com/v2/resize:fit:1200/1*8ZhgsyCNQRDaKEB7Rk_Udw.jpeg';
          transformedMsg.preview = getAbsoluteUrl(message_data.location_preview || decryptedText) || LocationPlaceholderImage2;
          transformedMsg.locationName = message_data.locationName || 'Shared Location';
          transformedMsg.locationUrl = (message_data.lat && message_data.lon)
            ? `https://www.google.com/maps/search/?api=1&query=${message_data.lat},${message_data.lon}`
            : '';
          transformedMsg.senderName = message_data.sender_name || message_data.member_name;
          break;
        case 'contact':
          transformedMsg.subtype = 'contact';
          transformedMsg.message = message_data.message || 'Contact shared';
          transformedMsg.contactName = data.contact_name || message_data.contact_name || decryptedText || 'Unknown Contact';
          transformedMsg.contactPhone = data.contact_phone_no || message_data.contact_phone_no || '';
          transformedMsg.contactEmail = data.contact_email || message_data.contact_email || '';
          break;
        case 'link':
          transformedMsg.subtype = 'link';
          transformedMsg.message = message_data.message || '';
          transformedMsg.preview = getAbsoluteUrl(message_data.preview_url || decryptedText);
          transformedMsg.linkTitle = message_data.link_title || 'Shared Link';
          transformedMsg.linkUrl = message_data.link_url || decryptedText;
          break;
        case 'reply':
          transformedMsg.subtype = 'reply';
          transformedMsg.message = decryptedText;
          transformedMsg.reply = message_data.reply_text || message_data.reply || '';
          break;
        default:
          transformedMsg.subtype = 'text';
          transformedMsg.message = decryptedText;
          break;
      }

      const isForActiveChat = (currentChatParams?.chat_type === "group" && message_data.group_id === currentChatParams?.receiverId) ||
        (currentChatParams?.chat_type === "single" && message_data.sender_id === currentChatParams?.receiverId);

      if (isForActiveChat && isIncoming) {
        if (messageType === 'isedited' || messageType === 'isdelete') {
          const updatedHistory = currentChatHistory.map(msg =>
            (msg.message_data?.message_id || msg.message_id) === (message_data.message_id || message_data.message_data?.message_id)
              ? { ...msg, message: transformedMsg.message, time: transformedMsg.time, message_data: transformedMsg.message_data }
              : msg
          );
          dispatch(SetChatHistory(updatedHistory));
        } else {
          dispatch(SetChatHistory([...currentChatHistory, transformedMsg]));
        }
        playMessageSound();
      } else if (isIncoming) {
        dispatch(IncrementMessageBadge());
        playMessageSound();
        const senderProfileImageUrl = message_data.sender_id
          ? `${PROFILE_IMAGE_URL}${message_data.sender_id}?t=${new Date().getTime()}`
          : null;
        const isNonAppUserMessage = message_data.nonAppUser === "1";

        toast.custom((t) => (
          <Box
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: 1,
              boxShadow: 3,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              pointerEvents: 'auto',
              minWidth: 300,
              maxWidth: 400,
              borderLeft: (theme) => `6px solid ${theme.palette.primary.main}`,
            }}
            onClick={() => {
              toast.dismiss(t.id);
              const targetId = message_data.sender_id;
              const targetName = transformedMsg.senderName;
              const myId = currentUserVal?.callingId || currentUserVal?.userId;
              const myName = `${currentUserVal?.firstName} ${currentUserVal?.lastName}`;

              dispatch(SetConversationUser({
                name: targetName,
                senderId: targetId,
                profilePicture: null
              }));
              dispatch(SetChatParams({
                receiverId: targetId,
                contact_name: targetName,
                senderId: myId,
                sender_name: myName,
                chat_type: 'single',
                isNonAppUser: isNonAppUserMessage,
              }));
              navigate(PATH_DASHBOARD.general.app);
            }}
          >
            <Avatar alt={transformedMsg.senderName} src={senderProfileImageUrl} />
            <Stack sx={{ ml: 2, overflow: 'hidden' }}>
              <Typography variant="subtitle2" noWrap>{transformedMsg.senderName}</Typography>
              <Typography variant="body2" color="text.secondary" noWrap>{transformedMsg.message}</Typography>
            </Stack>
          </Box>
        ), { position: 'top-right', duration: 5000 });
      }
      dispatch(SetChatListTrigger());
    });

    socket.on("chatreceived", (msg) => {
      dispatch(SetChatListTrigger());
    });

    socket.on("pagerReceived", (data) => {
      if (data && data.data) {
        dispatch(SetCurrentPagerData(data.data));
        dispatch(SetPagerModalOpen(true));
        dispatch(IncrementPagerBadge());
        dispatch(SetPagerListTrigger());
        playBeep('incoming');
      }
    });

    socket.on("incomingCall", (data) => {
      const callData = data.data;
      const currentActiveCallDetails = activeCallDetailsRef.current;
      const currentUserVal = currentUserRef.current;

      if (callData.call_type === 'created') {
        playBeep('incoming');
        dispatch(receiveIncomingCall({
          roomId: callData.room_id,
          callerId: callData.caller_id,
          callerName: callData.callerName,
          receiverId: callData.user_id,
          receiverName: currentUserVal?.firstName + ' ' + currentUserVal?.lastName,
          type: callData.type,
        }));
        setTimeout(() => {
          if (activeCallDetailsRef.current?.status === 'incoming') {
            console.log(`[SocketContext] Emitting 'denyCall' (auto-reject). Socket connected: ${socket.connected}, ID: ${socket.id}`);
            // Emit chatbox before denyCall
            if (currentUserRef.current?.callingId) {
              socket.emit("chatbox", { user_id: currentUserRef.current.callingId, timestamp: getCurrentTime() });
            }
            socket.emit("denyCall", { callerId: callData.caller_id, deviceId: navigator.platform });
            dispatch(denyCall());
            stopBeep();
          }
        }, 50000);
      } else if (callData.call_type === 'ended' || callData.call_type === 'platform' || callData.call_type === 'waiting') {
        const activeRoomId = currentActiveCallDetails?.roomId || currentActiveCallDetails?.room_id;
        if (activeRoomId === callData.room_id) {
          if (callData.call_type === 'platform' && currentActiveCallDetails?.status === 'ringing') {
            const myId = currentUserVal?.callingId || currentUserVal?.userId;
            if (String(callData.user_id) === String(myId)) return;
          }
          lastCallEndRef.current = Date.now();
          dispatch(endCall());
          stopBeep();
        }
      }
    });

    socket.on('callReject', (data) => {
      const currentActiveCallDetails = activeCallDetailsRef.current;
      if (currentActiveCallDetails?.status === 'ringing' && currentActiveCallDetails.receiverId === data.callerId) {
        dispatch(denyCall());
        stopBeep();
      }
    });

    socket.on('callDenied', (data) => {
      const currentActiveCallDetails = activeCallDetailsRef.current;
      if (currentActiveCallDetails?.status === 'ringing' && currentActiveCallDetails.receiverId === data.callerId) {
        dispatch(denyCall());
        stopBeep();
      } else if (currentActiveCallDetails?.status === 'incoming' && currentActiveCallDetails.callerId === data.callerId && data.deviceId !== navigator.platform) {
        dispatch(denyCall());
        stopBeep();
      }
    });

    socket.on('callAcceptedByOther', (data) => {
      const currentActiveCallDetails = activeCallDetailsRef.current;
      if (currentActiveCallDetails?.status === 'ringing' && currentActiveCallDetails.receiverId === data.data.callerId) {
        dispatch(denyCall());
        stopBeep();
      } else if (currentActiveCallDetails?.status === 'incoming' && currentActiveCallDetails.callerId === data.data.callerId && data.data.deviceId !== navigator.platform) {
        dispatch(denyCall());
        stopBeep();
      }
    });

    socket.on('callAccepted', (response) => {
      const currentActiveCallDetails = activeCallDetailsRef.current;
      const callData = response.data;
      if (currentActiveCallDetails?.status === 'ringing' || currentActiveCallDetails?.status === 'incoming') {
        stopBeep();
        dispatch(acceptCall({
          roomId: callData.roomId,
          callerId: callData.callerId,
          callerName: callData.callerName,
          receiverId: callData.receiverId,
          receiverName: callData.receiverName,
          type: callData.type,
        }));
      }
    });

    socket.on("endConversation", (data) => {
      if (data.chat_ended === true) {
        dispatch(SetNonAppChatEnded(true));
        dispatch(SetIsFirstMessageSentToNonAppUser(true));
        toast.info("Conversation ended by the other party.");
      } else {
        dispatch(SetNonAppChatEnded(false));
        dispatch(SetIsFirstMessageSentToNonAppUser(false));
      }
    });

    return () => {
      console.log("[SocketContext] useEffect cleanup. Disconnecting socket:", socket.id);
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      stopBeep();
      dispatch(resetCallState());
    };
  }, [dispatch, SOCKET_URL, currentUserId, currentUserEmail, currentUserCallingId, playBeep, stopBeep, navigate, callAddPatientRecordApi, emitChatbox]);

  const outgoingCallTimeoutRef = useRef(null);

  useEffect(() => {
    if (ringingModalOpen && activeCallDetails?.status === 'ringing') {
      if (outgoingCallTimeoutRef.current) {
        clearTimeout(outgoingCallTimeoutRef.current);
      }
      outgoingCallTimeoutRef.current = setTimeout(() => {
        if (activeCallDetailsRef.current?.status === 'ringing') {
          const currentActiveCall = activeCallDetailsRef.current;
          const currentUserVal = currentUserRef.current;

          if (socketRef.current && socketRef.current.connected) {
            // Emit makeCall with 'ended' to properly close the call on the server
            if (currentUserVal) {
              const callEndDetails = {
                call_id: currentActiveCall.roomId || currentActiveCall.room_id,
                chat_type: 'call',
                room_id: currentActiveCall.roomId || currentActiveCall.room_id,
                created_at: generateTimeStamp(),
                call_type: 'ended',
                platform: navigator.platform,
                phone: currentUserVal.userId || currentUserVal.id,
                user_id: currentActiveCall.receiverId,
                type: currentActiveCall.type,
                caller_id: currentActiveCall.callerId,
                callerName: currentActiveCall.callerName,
                receiverName: currentActiveCall.receiverName,
                receiverId: currentActiveCall.receiverId,
              };
              safeEmit("makeCall", callEndDetails);
              lastCallEndRef.current = Date.now();

              // Emit registercalls with 'missed' status to register the missed call
              const registerCallsPayload = {
                user_id: currentActiveCall.callerId,
                receiver_id: currentActiveCall.receiverId,
                status: "missed",
                message_data: {
                  caller_id: currentActiveCall.callerId,
                  call_id: currentActiveCall.roomId || currentActiveCall.room_id,
                  message: "wsPSnF20OYLRK9I2KjqtocP8JhjBfZge9Z+nEgPkknw=",
                  created_at: String(generateTimeStamp()),
                  call_type: "missed",
                  type: currentActiveCall.type,
                  chat_type: "call",
                  call_status: "missed"
                }
              };
              safeEmit("registercalls", registerCallsPayload);
            }
            safeEmit("denyCall", { callerId: currentActiveCall.receiverId, deviceId: navigator.platform });
          }
          dispatch(denyCall());
          stopBeep();
          alert(`Call to ${currentActiveCall.receiverName} timed out. No response.`);
        }
      }, 50000);
    } else {
      if (outgoingCallTimeoutRef.current) {
        clearTimeout(outgoingCallTimeoutRef.current);
        outgoingCallTimeoutRef.current = null;
      }
    }

    return () => {
      if (outgoingCallTimeoutRef.current) {
        clearTimeout(outgoingCallTimeoutRef.current);
        outgoingCallTimeoutRef.current = null;
      }
    };
  }, [ringingModalOpen, activeCallDetails, dispatch, stopBeep, safeEmit]);

  const disconnect = () => {
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.disconnect();
    }
  };

  const endConversation = useCallback((chatend, isNonAppUser) => {
    if (!socketRef.current || !chatParamsRef.current) {
      return;
    }

    const receiver_id = chatParamsRef.current.receiverId;
    const sender_id = chatParamsRef.current.senderId;

    dispatch(SetIsFirstMessageSentToNonAppUser(chatend));

    safeEmit("endConversation", {
      receiver_id: receiver_id,
      sender_id: sender_id,
      chat_ended: chatend,
    });
  }, [dispatch]);

  const sendChatMessage = useCallback(async (message, message_type, attachment = null, targetReceiverId = null, originalMessageId = null, originalChatTime = null, targetChatType = null, targetName = null) => {
    const connected = await ensureSocketConnection();
    if (!connected) {
      toast.error("Connection failed. Message not sent.");
      return;
    }

    if (!chatParamsRef.current?.senderId) {
      return;
    }

    const storedUserStr = sessionStorage.getItem('currentUser');
    const storedUser = storedUserStr ? JSON.parse(storedUserStr) : null;

    const receiver_id = targetReceiverId || chatParamsRef.current.receiverId;
    const sender_id = chatParamsRef.current.senderId;
    const sender_name = storedUser
      ? `${storedUser.firstName || ''} ${storedUser.lastName || ''}`.trim()
      : chatParamsRef.current.sender_name;
    const receiver_name = targetName || chatParamsRef.current.contact_name;
    // Use targetChatType if provided (for forwarding), otherwise use chatParamsRef
    const chat_type = targetChatType || chatParamsRef.current.chat_type || "single";
    const isNonAppUser = chatParamsRef.current.isNonAppUser ? "1" : "0";
    const time = getCurrentTime();
    const uuid = originalMessageId || generateUUID();

    const sortedIds = [String(sender_id), String(receiver_id)].sort();
    const chat_id = `${sortedIds[0]}${sortedIds[1]}`;

    let jsonobj = {
      chat_id: chat_id,
      chat_time: time,
      chat_type: chat_type,
      message_id: uuid,
      message_type: message_type,
      isDelete: (message_type === "isDelete" ? "1" : "0"),
      phone: receiver_name,
      read_status: 1,
      nonAppUser: isNonAppUser,
      receiver_id: receiver_id,
      sender_id: sender_id,
      user_id: sender_id,
      user_name: receiver_name,
      sender_name: sender_name,
      receiver_name: receiver_name,
    };

    const organizationName = storedUser?.organizationList?.[0]?.orgName || '';
    const isFirstMessage = isFirstMessageSentToNonAppUserRef.current;

    if (isNonAppUser === "1") {
      if (isFirstMessage) {
        jsonobj["isFirstMessage"] = true;
        jsonobj["organizationName"] = organizationName;
        endConversation(false, isNonAppUser);
      } else {
        endConversation(false, isNonAppUser);
      }
    }

    if (message_type === "location" && typeof attachment === 'object' && attachment !== null) {
      jsonobj.lat = String(attachment.lat);
      jsonobj.lon = String(attachment.lon);
      jsonobj.message = message;
      jsonobj.attachment = attachment.location_preview || '';
    } else if (message_type === "text" || message_type === "isEdited" || message_type === "isDelete") {
      jsonobj.message = message;
    } else {
      jsonobj.message = message;
      jsonobj.attachment = attachment;
    }

    if (['image', 'video', 'audio', 'document', 'file', 'sticker', 'gif'].includes(message_type)) {
      jsonobj.isDownload = "1";
      jsonobj.local_path = "0";
    }

    safeEmit("encryptMsg", {
      message_data: jsonobj,
      receiver_id: receiver_id,
      sender_id: sender_id,
      chat_type: chat_type,
    });

    const formattedTime = formatChatTime(jsonobj.chat_time);
    const newMessageForDisplay = {
      ...jsonobj,
      incoming: false,
      outgoing: true,
      senderName: sender_name,
      chatType: chat_type,
      time: formattedTime,
      message_id: jsonobj.message_id,
      message_data: {
        ...jsonobj,
        message_text: jsonobj.message,
        attachment: jsonobj.attachment,
        message_type: jsonobj.message_type,
        chat_time: parseInt(jsonobj.chat_time),
      }
    };

    if (message_type === 'isEdited' || message_type === 'isDelete') {
      const updatedHistory = chatHistoryRef.current.map(msg =>
        (msg.message_data?.message_id || msg.message_id) === originalMessageId
          ? { ...msg, message: newMessageForDisplay.message, time: newMessageForDisplay.time, message_data: newMessageForDisplay.message_data }
          : msg
      );
      dispatch(SetChatHistory(updatedHistory));
    } else {
      dispatch(SetChatHistory([...chatHistoryRef.current, newMessageForDisplay]));
    }
    dispatch(SetChatListTrigger());
    dispatch(ClearMessageToEdit());

    if (isNonAppUser === "1" && isFirstMessage) {
      dispatch(SetIsFirstMessageSentToNonAppUser(false));
    }

  }, [chatParamsRef, dispatch, ensureSocketConnection, endConversation, isFirstMessageSentToNonAppUserRef]);

  const sendGroupChatMessage = useCallback(async (message, message_type, attachment = null, targetGroupId = null, originalMessageId = null, originalChatTime = null, targetName = null) => {
    const connected = await ensureSocketConnection();
    if (!connected) {
      toast.error("Connection failed. Message not sent.");
      return;
    }

    if (!chatParamsRef.current?.senderId) {
      return;
    }

    const storedUserStr = sessionStorage.getItem('currentUser');
    const storedUser = storedUserStr ? JSON.parse(storedUserStr) : null;

    const group_id = targetGroupId || chatParamsRef.current.receiverId;
    const group_name = targetName || chatParamsRef.current.contact_name;
    const sender_id = chatParamsRef.current.senderId;
    const sender_name = storedUser
      ? `${storedUser.firstName || ''} ${storedUser.lastName || ''}`.trim()
      : chatParamsRef.current.sender_name;
    const chat_type = "group";
    const time = getCurrentTime();
    const uuid = originalMessageId || generateUUID();

    const chat_id = `${group_id}`;

    let jsonobj = {
      chat_id: chat_id,
      chat_time: time,
      chat_type: chat_type,
      message_id: uuid,
      member_id: sender_id,
      message_type: message_type,
      isDelete: (message_type === "isDelete" ? "1" : "0"),
      member_role: "1",
      member_no: sender_name,
      member_name: sender_name,
      group_name: group_name,
      group_id: group_id,
      chat_id: chat_id,
    };

    if (message_type === "location" && typeof attachment === 'object' && attachment !== null) {
      jsonobj.lat = String(attachment.lat);
      jsonobj.lon = String(attachment.lon);
      jsonobj.message = message;
      jsonobj.attachment = attachment.location_preview || '';
    } else if (message_type === "text" || message_type === "isEdited" || message_type === "isDelete") {
      jsonobj.message = message;
    } else {
      jsonobj.message = message;
      jsonobj.attachment = attachment;
    }

    const formattedTime = formatChatTime(jsonobj.chat_time);
    const newMessageForDisplay = {
      ...jsonobj,
      incoming: false,
      outgoing: true,
      senderName: sender_name,
      chatType: chat_type,
      time: formattedTime,
      message_id: jsonobj.message_id,
      message_data: {
        ...jsonobj,
        message_text: jsonobj.message,
        attachment: jsonobj.attachment,
        message_type: jsonobj.message_type,
        chat_time: parseInt(jsonobj.chat_time),
      }
    };

    if (message_type === 'isEdited' || message_type === 'isDelete') {
      const updatedHistory = chatHistoryRef.current.map(msg =>
        (msg.message_data?.message_id || msg.message_id) === originalMessageId
          ? { ...msg, message: newMessageForDisplay.message, time: newMessageForDisplay.time, message_data: newMessageForDisplay.message_data }
          : msg
      );
      dispatch(SetChatHistory(updatedHistory));
    } else {
      dispatch(SetChatHistory([...chatHistoryRef.current, newMessageForDisplay]));
    }

    safeEmit("encryptMsg", {
      message_data: jsonobj,
      receiver_id: group_id,
      sender_id: sender_id,
      chat_type: chat_type
    });
    dispatch(SetChatListTrigger());
    dispatch(ClearMessageToEdit());
  }, [chatParamsRef, dispatch, ensureSocketConnection]);

  const makeCall = useCallback(async (receiverName, receiverId, type) => {
    const storedUserStr = sessionStorage.getItem('currentUser');
    const currentUserLatest = storedUserStr ? JSON.parse(storedUserStr) : currentUser;

    if (!currentUserLatest || !currentUserLatest.callingId) {
      alert("User information missing. Please log in again.");
      return;
    }

    const connected = await ensureSocketConnection();
    if (!connected) {
      alert("Cannot place call: Socket connection not established. Please check your network or try again later.");
      dispatch(denyCall());
      stopBeep();
      return;
    }

    const resp = await getDisabledStatus(receiverId);
    if (resp.callStatus === "true" && resp.data && resp.data.doNotDisturbEnable === true) {
      alert(`${receiverName} has Do Not Disturb enabled and cannot be contacted at this time.`);
      return;
    }

    const callerName = `${currentUserLatest.firstName} ${currentUserLatest.lastName}`;
    const callerId = currentUserLatest.callingId;
    const userId = currentUserLatest.userId || currentUserLatest.id;
    const createdAt = generateTimeStamp();
    const roomId = generateRoomId(callerId);

    const callDetails = {
      call_id: roomId,
      chat_type: 'call',
      room_id: roomId,
      created_at: createdAt,
      call_type: 'created',
      platform: "ios",
      phone: userId,
      user_id: receiverId,
      type: type,
      caller_id: callerId,
      callerName: callerName,
      callerId: callerId,
      receiverName: receiverName,
      receiverId: receiverId,
    };

    dispatch(startOutgoingCall(callDetails));
    playBeep('outgoing');

    safeEmit("makeCall", callDetails);

    if (receiverName.startsWith("+1")) {
      const message = `${type === 'video' ? 'Video' : 'Audio'} Call Request_${roomId}`;
      sendTextCallRequest(receiverId, callerId, type === 'video', receiverName, message);
    }
  }, [currentUser, dispatch, playBeep, stopBeep, ensureSocketConnection]);

  const denyCurrentCall = useCallback((callerId, deviceId = navigator.platform) => {
    if (!socketRef.current || !activeCallDetailsRef.current) {
      return;
    }

    const currentActiveCall = activeCallDetailsRef.current;
    const currentUserVal = currentUserRef.current;

    // If this is an outgoing call being canceled (status === 'ringing'), also emit makeCall with 'ended'
    if (currentActiveCall.status === 'ringing' && currentUserVal) {
      const callEndDetails = {
        call_id: currentActiveCall.roomId || currentActiveCall.room_id,
        chat_type: 'call',
        room_id: currentActiveCall.roomId || currentActiveCall.room_id,
        created_at: generateTimeStamp(),
        call_type: 'ended',
        platform: navigator.platform,
        phone: currentUserVal.userId || currentUserVal.id,
        user_id: currentActiveCall.receiverId,
        type: currentActiveCall.type,
        caller_id: currentActiveCall.callerId,
        callerName: currentActiveCall.callerName,
        receiverName: currentActiveCall.receiverName,
        receiverId: currentActiveCall.receiverId,
      };
      safeEmit("makeCall", callEndDetails);
      lastCallEndRef.current = Date.now();

      // Emit registercalls with 'missed' status to register the missed call
      const registerCallsPayload = {
        user_id: currentActiveCall.callerId,
        receiver_id: currentActiveCall.receiverId,
        status: "missed",
        message_data: {
          caller_id: currentActiveCall.callerId,
          call_id: currentActiveCall.roomId || currentActiveCall.room_id,
          message: "wsPSnF20OYLRK9I2KjqtocP8JhjBfZge9Z+nEgPkknw=",
          created_at: String(generateTimeStamp()),
          call_type: "missed",
          type: currentActiveCall.type,
          chat_type: "call",
          call_status: "missed"
        }
      };
      safeEmit("registercalls", registerCallsPayload);
    }

    safeEmit("denyCall", { callerId, deviceId });
    stopBeep();
    dispatch(denyCall());
  }, [dispatch, stopBeep, safeEmit]);

  const acceptCurrentCall = useCallback((callDetails) => {
    // Read currentUser directly from sessionStorage instead of using stale state
    const storedUser = sessionStorage.getItem('currentUser');
    const currentUserFromStorage = storedUser ? JSON.parse(storedUser) : null;

    if (!socketRef.current || !currentUserFromStorage || !activeCallDetailsRef.current) {
      return;
    }
    const deviceId = navigator.platform;
    const { roomId, callerId, receiverId, type } = callDetails;

    safeEmit('acceptCall', {
      callerId: callerId,
      deviceId: deviceId,
      roomId: roomId,
      receiverId: receiverId,
      type: type,
    });
    stopBeep();
    dispatch(acceptCall(callDetails));
  }, [dispatch, stopBeep, safeEmit]);

  const endCurrentCall = useCallback(() => {
    if (!socketRef.current || !activeCallDetailsRef.current || !currentUser || !currentUser.callingId) {
      return;
    }

    const { roomId, callerId, receiverId, type, callerName, receiverName } = activeCallDetailsRef.current;
    const currentUserId = currentUser.callingId;

    const senderOfEndCall = (callerId === currentUserId) ? callerId : receiverId;
    const receiverOfEndCall = (callerId === currentUserId) ? receiverId : callerId;

    const callEndDetails = {
      call_id: roomId,
      chat_type: 'call',
      room_id: roomId,
      created_at: generateTimeStamp(),
      call_type: 'ended',
      platform: navigator.platform,
      phone: (callerId === currentUserId) ? currentUser.userId : activeCallDetailsRef.current.phone,
      user_id: receiverOfEndCall,
      type: type,
      caller_id: senderOfEndCall,
      callerName: (callerId === currentUserId) ? callerName : receiverName,
      receiverName: (callerId === currentUserId) ? receiverName : callerName,
      receiverId: receiverOfEndCall,
    };

    safeEmit("makeCall", callEndDetails);
    lastCallEndRef.current = Date.now();
    stopBeep();
    dispatch(endCall());
  }, [dispatch, currentUser, stopBeep]);

  const sendTextCallRequest = useCallback((receiver_id, sender_id, isVideoCall, receiverName, message) => {
    if (!socketRef.current || !currentUser) {
      return;
    }

    const sortedIds = [String(sender_id), String(receiver_id)].sort();
    const chat_id = `${sortedIds[0]}${sortedIds[1]}`;

    safeEmit("encryptMsg", {
      message_data: {
        sender_id: sender_id,
        receiver_id: receiver_id,
        chat_id: chat_id,
        message: message,
        message_type: "text",
      },
    });
  }, [currentUser]);

  // Added sendPager function
  const sendPager = useCallback(async (receiverUserId, receiverFullName = "User") => {
    const storedUserStr = sessionStorage.getItem('currentUser');
    const currentUserData = storedUserStr ? JSON.parse(storedUserStr) : currentUser;

    if (!currentUserData) {
      toast.error('User not logged in. Please log in again.');
      return;
    }

    const senderUserId = currentUserData.userId || currentUserData.id;
    const senderName = `${currentUserData.firstName || ''} ${currentUserData.lastName || ''}`.trim();
    const organizationId = currentUserData.organizationList?.[0]?.orgId;
    const userEmail = currentUserData.email;

    if (!senderUserId || !senderName || !organizationId || !userEmail || !receiverUserId) {
      toast.error('Missing details to send pager.');
      return;
    }

    // Check socket connection first
    const connected = await ensureSocketConnection();
    if (!connected) {
      console.log('[SocketContext] Socket.IO is NOT connected. Pager not sent.');
      toast.error("Cannot send pager: Connection not established.");
      return;
    }

    try {
      const payload = {
        organizationId: organizationId,
        senderName: senderName,
        senderUserId: senderUserId,
        receiverUserId: receiverUserId,
      };

      const response = await axios.post('https://backend-stgweb.meftii.com/sendpager', payload, {
        headers: {
          email: userEmail,
        },
      });

      if (response.data?.callStatus === 'true' && response.data?.resultCode === '0000') {
        toast.success(`Pager sent to ${receiverFullName} successfully!`);
      } else {
        toast.error(response.data?.resultDesc || 'Failed to send pager.');
      }
    } catch (error) {
      console.error('Error sending pager:', error);
      toast.error('An error occurred while sending the pager.');
    }
  }, [currentUser, ensureSocketConnection]);

  return (
    <SocketContext.Provider
      value={{
        socket: socketRef.current,
        isConnected,
        connect,
        disconnect,
        sendChatMessage,
        sendGroupChatMessage,
        makeCall,
        denyCall: denyCurrentCall,
        acceptCall: acceptCurrentCall,
        endCurrentCall,
        sendTextCallRequest,
        playBeep,
        stopBeep,
        endConversation,
        sendPager, // Expose sendPager
        safeEmit,
        BASE_URL,
        CALLING_URL,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export { SocketContext };

export default SocketProvider;